import {APIClass} from 'aws-amplify';
import {FC, useState} from 'react';
import {Button, Form, Input, message, Modal} from 'antd';
import {DEFAULT_API_NAME, useApi} from '../../util/Auth';
import {FormSubmitButton} from '../FormSubmitButton';
import {RedoOutlined} from '@ant-design/icons';

/** Generates a new password, in the form `pMymic-gqbenn-jnxd5n`: three groups
 * of alphanumeric characters, separated by dashes. Some chars are not included
 * to avoid confusion: l, O, 0, 1.
 */
const generateNewPassword = (
  nbGroups = 3,
  groupLength = 6,
  sep = '-',
): string => {
  // no l, O, 0 and 1 to avoid confusion
  const LOWER = 'abcdefghijkmnopqrstuvwxyz';
  const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const DIGIT = '23456789';
  // const ALL = LOWER + UPPER + DIGIT;

  /** Returns a random number between [0;max[ */
  const getRandom = (max: number): number => {
    const arr = new Uint32Array(1);
    crypto.getRandomValues(arr);
    return arr[0] % max;
  };

  /** Picks a random character in a string. */
  const pick = (str: string): string => str[getRandom(str.length)];

  const shuffle = (arr: string[]): string[] => {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = getRandom(i + 1);
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  const chars: string[] = [
    // at least one digit + one uppercase per password
    pick(DIGIT),
    pick(UPPER),
  ];

  for (let i = chars.length; i < nbGroups * groupLength; i++) {
    chars.push(pick(LOWER)); // rest of password is only LOWER chars for now
  }
  const shuffled = shuffle(chars).join('');

  const parts: string[] = [];
  for (let i = 0; i < shuffled.length; i += groupLength) {
    parts.push(shuffled.slice(i, i + groupLength));
  }

  return parts.join(sep);
};

const changeUserPassword = async (
  api: APIClass | null,
  email: string,
  password: string,
): Promise<string | undefined> => {
  if (!api) return;

  const res = await api.post(DEFAULT_API_NAME, `/admin/changeUserPassword`, {
    body: {
      email,
      password,
    },
  });

  if (!res) {
    throw new Error("Impossible de changer le mdp de l'utilisateur");
  }

  if (!!res.error) {
    throw new Error(res.error);
  }

  return res;
};

export const SuperAdminChangePassword: FC = (props) => {
  const api = useApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState<string>();

  const [form] = Form.useForm();

  return (
    <div>
      <Form
        form={form}
        layout="inline"
        initialValues={{password: generateNewPassword()}}
        onFinish={async (values) => {
          if (loading || !values.email || !values.password) {
            return;
          }

          Modal.confirm({
            title: "Changer le mot de passe de l'utilisateur ?",
            content: (
              <p>
                Le mot de passe de l'utilisateur sera modifié instantanément.
                L'utilisateur ne recevra aucun email.
              </p>
            ),
            onOk: async () => {
              setError('');
              setLoading(true);
              try {
                const info = await changeUserPassword(
                  api,
                  values.email,
                  values.password,
                );
                setInfo(info);
              } catch (err: any) {
                const msg = err.response?.data ?? err?.message ?? err;
                message.warning(msg);
                setInfo(undefined);
                setError(msg);
              }
              setLoading(false);
            },
          });
        }}
      >
        <Form.Item
          label="Email utilisateur"
          name="email"
          rules={[{required: true}]}
        >
          <Input type="email" />
        </Form.Item>
        <Form.Item
          label="Nouveau mdp"
          name="password"
          rules={[{required: true}]}
        >
          <Input />
        </Form.Item>

        <Button
          icon={<RedoOutlined />}
          onClick={() => form.setFieldsValue({password: generateNewPassword()})}
        />

        <Form.Item>
          <FormSubmitButton loading={loading}>Changer le mdp</FormSubmitButton>
        </Form.Item>
      </Form>

      {error}

      {info ? <div>{JSON.stringify(info)}</div> : undefined}
    </div>
  );
};
